import { type HTMLAttributes } from "react";
import { clsx } from "clsx";

export type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

const tones: Record<BadgeTone, string> = {
  neutral: "bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]",
  // accent text was --color-accent (orange-55 #FF4B19) on --color-accent-subtle
  // (orange-95 #FFEBE5) — 2.91:1, the same class of failure as `success` and
  // `danger` below and the last of the four to be fixed. --color-accent-on-subtle
  // is orange-30 in light (7.10:1) and stays bright in dark, where the tint is a
  // wash over a dark surface and the bright accent is what reads.
  accent: "bg-[var(--color-accent-subtle)] text-[var(--color-accent-on-subtle)]",
  // success text was --color-success-500 (bright green #0AC275) on --color-success-100 (darker
  // green #089158) — 1.72:1, an axe "serious" contrast failure (client Overview redesign,
  // 2026-07-07). --color-neutral-900 (#191A1B) closes most of the gap but still lands at 4.31:1
  // (just under the 4.5:1 AA threshold) on this background — --color-neutral-950 (true black)
  // is needed to clear it.
  success: "bg-[var(--color-success-100)] text-[var(--color-neutral-950)]",
  warning: "bg-[var(--color-warning-100)] text-[var(--color-neutral-900)]",
  // danger text was --color-danger-500 (red-60 #FF3333) on --color-danger-100 (red-70 #FF6666) — two
  // near-identical reds (unreadable). Use dark neutral-900 text on the red bg, mirroring `warning`.
  danger: "bg-[var(--color-danger-100)] text-[var(--color-neutral-900)]",
};

export function Badge({ tone = "neutral", className, ...props }: BadgeProps) {
  return (
    <span
      className={clsx(
        // whitespace-nowrap: a multi-word badge (e.g. "Egress denied") must not wrap and overflow the pill
        "inline-flex items-center whitespace-nowrap px-2 h-6 rounded-xs text-xs font-medium",
        tones[tone],
        className,
      )}
      {...props}
    />
  );
}
